@if (state() === 'connected') {
	<mat-dialog-content class="max-w-xl">
		<div class="flex flex-col items-center py-6 text-center">
			<mat-icon class="!h-24 !w-24 !text-8xl text-ss-green-600" fontIcon="fa-link" fontSet="fa-solid"></mat-icon>
			<p class="mt-4 text-lg">{{facility().name}}'s Simpli360 and PointClickCare are integrated.</p>
		</div>
	</mat-dialog-content>
	<mat-dialog-actions align="end">
		<button mat-button mat-dialog-close>Close</button>
	</mat-dialog-actions>
} @else if (state() === 'instructions') {
	<mat-dialog-content class="max-w-xl">
		<p class="mb-4">
			Connect your facility to PointClickCare to allow team members to easily send Simpli360 generated documents and reports to patients in PointClickCare.
		</p>
		<ol class="mb-4 list-decimal space-y-6 pl-5">
			<li>
				<strong class="text-base">Request Access</strong><br>
				<span class="text-gray-600">Request the Simpli360 integration from the PointClickCare Marketplace.</span>
				<div class="mt-2">
					<a href="https://marketplace.pointclickcare.com/s/partner-app/aFCHs00000005wgOAA/simpli360" target="_blank" rel="noopener noreferrer" mat-raised-button class="!bg-ss-blue-500 !text-white hover:no-underline">
						<mat-icon fontIcon="fa-external-link" fontSet="fa-solid" class="text-sm"></mat-icon>
						PointClickCare Marketplace
					</a>
				</div>
			</li>
			<li>
				<strong class="text-base">Wait for Approval</strong><br>
				<span class="text-gray-600">SimpliScreen and PointClickCare will review your request. This usually takes a few days.</span>
			</li>
			<li>
				<strong class="text-base">Sign In with PointClickCare</strong><br>
				<span class="text-gray-600">Once approved, sign out and sign back in using <strong>"Sign in with PointClickCare"</strong>. Then return here to finish setup.</span>
			</li>
		</ol>
	</mat-dialog-content>
	<mat-dialog-actions align="end">
		<button mat-button mat-dialog-close>Close</button>
	</mat-dialog-actions>
} @else if (state() === 'match-error') {
	<mat-dialog-content class="max-w-xl">
		<div class="flex flex-col items-center py-6 text-center">
			<mat-icon class="!h-24 !w-24 !text-8xl text-ss-red" fontIcon="fa-circle-exclamation" fontSet="fa-solid"></mat-icon>
			<p class="mt-4 text-lg">We could not find the PointClickCare facility configured for this integration.</p>
			<p class="mt-2 text-sm text-gray-600">Something may be wrong with the integration setup. Please contact support at <a class="text-ss-blue-500 hover:underline" href="mailto:concierge@simpliscreen.com">concierge&#64;simpliscreen.com</a>.</p>
		</div>
	</mat-dialog-content>
	<mat-dialog-actions align="end">
		<button mat-button mat-dialog-close>Close</button>
	</mat-dialog-actions>
} @else {
	<mat-dialog-content class="flex h-120 flex-col">
		<mat-stepper [linear]="true" #stepper class="grid h-full min-h-0">
			<mat-step [label]="matchedByPendingIds ? 'Review Integration' : 'Select Facility'" [editable]="canGoBack()" [stepControl]="matchedByPendingIds ? null : linkPccFacilityForm.form">
				<form id="linkPccFacilityForm" class="grid w-160 gap-3" #linkPccFacilityForm="ngForm">
					@if (matchedByPendingIds) {
						<p class="text-sm text-gray-600">
							Your SimpliScreen team has approved and configured the PointClickCare integration for this facility.
							Review the facilities below, then continue to verify patients and complete the link.
						</p>
						<div class="mb-2 flex items-stretch gap-6">
							<div class="flex-1">
								<div class="flex h-full flex-col rounded-lg border-2 border-ss-orange p-4">
									<div class="mb-4 flex items-center justify-between border-b border-ss-orange pb-3">
										<div class="font-medium">Simpli360 Facility</div>
										<mat-icon fontIcon="fa-link" fontSet="fa-solid"></mat-icon>
									</div>
									<div class="space-y-2 text-sm">
										<div class="flex justify-between gap-4">
											<span class="font-medium">Name:</span>
											<span class="text-right">{{facility().name}}</span>
										</div>
										<div class="flex justify-between gap-4">
											<span class="font-medium">Address:</span>
											<span class="grid items-center text-right">
												@if (facility().addressLine1) {
													{{facility().addressLine1}}<br>{{facility().city}}, {{facility().state?.abbreviation}} {{facility().zip}}
												} @else {
													<span class="text-gray-400">—</span>
												}
											</span>
										</div>
									</div>
								</div>
							</div>
							<mat-icon class="flex-none self-center text-gray-400" fontIcon="fa-link" fontSet="fa-solid"></mat-icon>
							<div class="flex-1">
								<div class="flex h-full flex-col rounded-lg border-2 border-ss-blue-400 p-4">
									<div class="mb-4 flex items-center justify-between border-b border-ss-blue-400 pb-3">
										<div class="font-medium">PointClickCare Facility</div>
										<mat-icon fontIcon="fa-hospital" fontSet="fa-solid"></mat-icon>
									</div>
									<div class="space-y-2 text-sm">
										<div class="flex justify-between gap-4">
											<span class="font-medium">Name:</span>
											<span class="text-right">{{fullSelectedPccFacility()?.facilityName}}</span>
										</div>
										<div class="flex justify-between gap-4">
											<span class="font-medium">Address:</span>
											<span class="text-right">
												@if (fullSelectedPccFacility()?.addressLine1) {
													{{fullSelectedPccFacility()?.addressLine1}}<br>{{fullSelectedPccFacility()?.city}}, {{fullSelectedPccFacility()?.state}} {{fullSelectedPccFacility()?.postalCode}}
												} @else {
													—
												}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div class="max-w-full flex-none rounded border border-solid border-gray-300 bg-gray-50 p-4">
							<div class="flex items-center gap-x-4">
								<mat-icon class="flex-none text-gray-600" fontIcon="fa-circle-info" fontSet="fa-solid"></mat-icon>
								<span class="text-sm text-gray-800">
									These facilities were paired during integration setup and cannot be changed here.
									If this does not look correct, please contact support at <a class="text-blue-400 hover:underline" href="mailto:concierge@simpliscreen.com">concierge&#64;simpliscreen.com</a>.
								</span>
							</div>
						</div>
					} @else {
						<p class="text-sm text-gray-500">Link the facility "{{facility().name}}" to PointClickCare</p>
						<ss-select-with-description [(ngModel)]="selectedFacilityOption" name="pccFacility" [options]="unlinkedPccFacilitiesOptionsConfig()" label="PointClickCare Facility" required/>
						<div class="max-w-full flex-none rounded border border-solid border-blue-400 bg-blue-50 p-4">
							<div class="flex items-center gap-x-4">
								<mat-icon class="text-blue-600" fontIcon="fa-circle-info" fontSet="fa-solid"></mat-icon>
								<span class="text-sm text-blue-800">
									@if (!hasUnlinkedPccFacilities()) {
										All PointClickCare facilities in your organization are already linked in Simpli360. Please contact support if this looks incorrect.
									} @else if (!foundFacilityByName) {
										No exact match was found for "{{facility().name}}" in your PointClickCare organization.<br><br> Please select the correct facility from the dropdown above.
									} @else {
										We found a matching PointClickCare facility named "{{facility().name}}" and pre-selected it for you.<br><br> You can change this selection if needed.
									}
								</span>
							</div>
						</div>
					}
				</form>
				<div class="mt-auto flex justify-end gap-x-2 py-2">
					<button mat-button mat-dialog-close type="button">Cancel</button>
					<button mat-raised-button color="primary" matStepperNext [disabled]="matchedByPendingIds ? !fullSelectedPccFacility() : (!hasUnlinkedPccFacilities() || linkPccFacilityForm.form.invalid)">Next</button>
				</div>
			</mat-step>
			<mat-step [label]="'Verify Patients'" [editable]="canGoBack()" class="flex h-full flex-col">
				<p class="w-150 pb-2 text-sm text-gray-500">Please double check the patients to ensure they are correct. We will attempt to link each patient in PointClickCare to a patient in Simpli360 using their birthdate and name. For patients in PointClickCare without a match in Simpli360, we will create a matching patient in Simpli360.</p>
				<div class="flex h-full flex-1 flex-col overflow-auto border-t border-solid border-gray-300">
					@for (patient of pagedPatients(); track patient.patientId) {
						<div class="flex h-full items-center gap-4 border-x border-b border-solid border-gray-300 px-4 py-2" [class.border-b-0]="$last">
							<div class="flex h-full items-center justify-center"><mat-icon fontIcon="fa-user" fontSet="fa-solid"></mat-icon></div>
							<div>
								<div class="truncate">{{patient.firstName}} {{patient.lastName}}</div>
								<div class="text-sm text-gray-500">DOB: {{patient.birthDate | date : 'shortDate'}}</div>
							</div>
						</div>
					}
					@if (loadingPatients()) {
						<div class="flex h-full items-center justify-center">
							<mat-spinner class="justify-self-center" diameter="80" />
						</div>
					}
				</div>
				<mat-paginator class="flex-none border border-solid border-gray-300" [pageSize]="PAGE_SIZE" [showFirstLastButtons]="true" [length]="patients().length" (page)="page.set($event.pageIndex)"></mat-paginator>
				<div class="mt-auto flex flex-none justify-end gap-x-2 py-2">
					<button mat-button mat-dialog-close type="button">Cancel</button>
					<button mat-raised-button color="primary" matStepperPrevious>Back</button>
					<button mat-raised-button color="primary" matStepperNext>Next</button>
				</div>
			</mat-step>
			<mat-step [label]="'Confirm Link'" [editable]="canGoBack()" [stepControl]="confirmLinkForm.form" class="flex h-full flex-col">
				<form id="confirmLinkForm" class="grid w-150 gap-4" #confirmLinkForm="ngForm">
					<p class="text-sm text-gray-600">
						Review the information below before continuing. This will link this Simpli360 facility to the selected PointClickCare facility and import or match patients.
					</p>

					<div class="grid grid-cols-2 gap-4">
						<div class="rounded-lg border-2 border-ss-orange p-4">
							<div class="mb-3 flex items-center justify-between border-b border-ss-orange pb-2">
								<div class="font-medium">Simpli360 Facility</div>
								<mat-icon fontIcon="fa-link" fontSet="fa-solid"></mat-icon>
							</div>
							<div class="flex justify-between text-sm">
								<span class="font-medium">Name:</span>
								<span>{{facility().name}}</span>
							</div>
						</div>

						<div class="rounded-lg border-2 border-ss-blue-400 p-4">
							<div class="mb-3 flex items-center justify-between border-b border-ss-blue-400 pb-2">
								<div class="font-medium">PointClickCare Facility</div>
								<mat-icon fontIcon="fa-hospital" fontSet="fa-solid"></mat-icon>
							</div>
							<div class="flex justify-between text-sm">
								<span class="font-medium">Name:</span>
								<span>{{fullSelectedPccFacility()?.facilityName}}</span>
							</div>
						</div>
					</div>

					<div class="rounded border border-solid border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
						This operation updates the facility link and starts patient matching/import. Please confirm carefully before continuing.
					</div>

					<mat-form-field class="w-full">
						<mat-label>Type 'LINK' to continue</mat-label>
						<input matInput #confirmTextInput="ngModel" [(ngModel)]="confirmText" name="confirmText" required pattern="^LINK$">
						<mat-error>Please type 'LINK' exactly to continue</mat-error>
					</mat-form-field>
				</form>
				<div class="mt-auto flex flex-none justify-end gap-x-2 py-2">
					<button mat-button mat-dialog-close type="button">Cancel</button>
					<button mat-raised-button color="primary" matStepperPrevious>Back</button>
					<button mat-raised-button color="warn" (click)="submitLink(confirmTextInput, stepper)">Link Facility</button>
				</div>
			</mat-step>
			<mat-step [label]="finalStep()" class="flex h-full flex-col" [editable]="false">
				@if (linkingPatients()) {
					<p class="w-150 pb-2 text-sm text-gray-500">We are linking the patients to the facility. This may take a few minutes.</p>
					<div class="flex h-full items-center justify-center">
						<mat-spinner class="justify-self-center" diameter="150" />
					</div>
				} @else if (error()) {
					<p class="w-150 pb-2 text-sm text-gray-500">We were unable to link the patients to the facility. Please try again.</p>
				} @else {
					<p class="w-150 pb-2 text-sm text-gray-500">We have linked the patients to the facility.</p>
					<ul class="list-inside list-disc">
						<li>Found by birthday and name: {{results().foundByBirthdayAndName}}</li>
						<li>Found by PointClickCare patient ID: {{results().foundByPccPatientId}}</li>
						<li>New Patients: {{results().notFound}}</li>
					</ul>
				}
				<div class="mt-auto flex flex-none justify-end gap-x-2 py-2">
					<button mat-button mat-dialog-close type="button">Close</button>
				</div>
			</mat-step>
		</mat-stepper>
	</mat-dialog-content>
}


